import { TimestampLike } from "./GenericDeviceAssignmentUtils";

interface AssignmentWithEndDate<T extends TimestampLike> {
  assignmentEndDate: T | null;
}

/**
 * Checks whether a counting device has any dependent assignments that are still
 * active (open, i.e. `assignmentEndDate == null`).
 *
 * @param isCountingDevice  Whether the device is a counting device (`electricityMeterForOtherDevices`).
 * @param countedAssignments  All assignments that reference this counting device via `externalElectricityMeterInfo.assignmentId`.
 * @returns `true` if the device is a counting device and at least one dependent assignment is still open.
 */
export function hasActiveDependentAssignments<T extends TimestampLike>(
  isCountingDevice: boolean,
  countedAssignments: AssignmentWithEndDate<T>[],
): boolean {
  return isCountingDevice &&
    countedAssignments.some(a => a.assignmentEndDate === null);
}

/**
 * Finds the latest `assignmentEndDate` among all dependent (counted) assignments.
 * Returns `null` if the device is not a counting device, there are no dependents,
 * or all dependents are still open.
 *
 * @param isCountingDevice  Whether the device is a counting device.
 * @param countedAssignments  All assignments that reference this counting device.
 * @returns The latest end date among released dependents, or `null`.
 */
export function getLatestDependentAssignmentEndDate<T extends TimestampLike>(
  isCountingDevice: boolean,
  countedAssignments: AssignmentWithEndDate<T>[],
): T | null {
  if (!isCountingDevice || countedAssignments.length === 0) {
    return null;
  }

  let latest: T | null = null;
  for (const assignment of countedAssignments) {
    if (!assignment.assignmentEndDate) continue;
    if (!latest || assignment.assignmentEndDate.toMillis() > latest.toMillis()) {
      latest = assignment.assignmentEndDate;
    }
  }
  return latest;
}

/**
 * Validates whether a counting device release is allowed given its dependent assignments.
 *
 * @param isCountingDevice  Whether the device is a counting device.
 * @param countedAssignments  All dependent assignments referencing this device.
 * @param proposedEndDate  The proposed end date for the counting device assignment (null = not releasing).
 * @returns An object with `valid: true` if allowed, or `valid: false` with a `reason` string.
 */
export function validateCountingDeviceRelease<T extends TimestampLike>(
  isCountingDevice: boolean,
  countedAssignments: AssignmentWithEndDate<T>[],
  proposedEndDate: T | null,
): { valid: true } | { valid: false; reason: "active_dependents" | "end_date_too_early"; latestDependentEndDate?: T } {
  if (!isCountingDevice || !proposedEndDate) {
    return { valid: true };
  }

  if (hasActiveDependentAssignments(isCountingDevice, countedAssignments)) {
    return { valid: false, reason: "active_dependents" };
  }

  const latestEnd = getLatestDependentAssignmentEndDate(isCountingDevice, countedAssignments);
  if (latestEnd && proposedEndDate.toMillis() < latestEnd.toMillis()) {
    return { valid: false, reason: "end_date_too_early", latestDependentEndDate: latestEnd };
  }

  return { valid: true };
}

// ─── Counting device date constraint helpers ──────────────────────

interface CountingAssignmentWindow<T extends TimestampLike> {
  assignmentStartDate: T;
  assignmentEndDate: T | null;
  effectiveMaxEndDate: T | null;
}

/**
 * Resolves the effective coverage window for a counting assignment.
 * For closed assignments, uses `assignmentEndDate`.
 * For open assignments with a successor, falls back to `effectiveMaxEndDate`.
 *
 * @returns `{ start, end }` where `end` may be null (truly open with no successor).
 */
export function computeCountingDeviceCoverage<T extends TimestampLike>(
  assignment: CountingAssignmentWindow<T>,
): { start: T; end: T | null } {
  return {
    start: assignment.assignmentStartDate,
    end: assignment.assignmentEndDate ?? assignment.effectiveMaxEndDate,
  };
}

/**
 * Checks whether a counting assignment candidate should be disabled because
 * its effective window ends before the earliest allowed start date of the
 * counted (dependent) device.
 *
 * Only applies in create mode — corrections may reference any candidate.
 *
 * @param effectiveMaxEndDate  The counting assignment's effective max end date (null = no constraint).
 * @param minDate  The earliest allowed start date for the counted device (null = no constraint).
 * @returns `true` if the candidate is incompatible and should be disabled.
 */
export function isCountingCandidateDisabledByMinDate<T extends TimestampLike>(
  effectiveMaxEndDate: T | null,
  minDate: T | null,
): boolean {
  if (!effectiveMaxEndDate || !minDate) return false;
  return effectiveMaxEndDate.toMillis() < minDate.toMillis();
}

/**
 * Checks whether a proposed date for a counted device falls within the
 * counting device's effective window (inclusive on both ends, day-based).
 *
 * @param date  The proposed start or end date for the counted device.
 * @param countingStart  The counting device assignment's start date.
 * @param countingEnd  The counting device's effective end date (null = no upper bound).
 * @returns `true` if the date is within the window.
 */
export function isDateWithinCountingWindow<T extends TimestampLike>(
  date: T,
  countingStart: T,
  countingEnd: T | null,
): boolean {
  if (date.toMillis() < countingStart.toMillis()) return false;
  if (countingEnd && date.toMillis() > countingEnd.toMillis()) return false;
  return true;
}

/**
 * Determines whether strict meter reading enforcement should be bypassed
 * for a device assignment. Enforcement is skipped when the device is counted
 * via an external electricity meter (dependent device) or when the device's
 * power consumption is not used for electricity billing.
 *
 * @param countedViaExternalDevice  Whether the device references an external electricity meter.
 * @param useForElectricityBilling  Whether the device's consumption is used for billing.
 * @returns `true` if meter reading enforcement should be skipped.
 */
export function shouldSkipMeterReadingEnforcement(
  countedViaExternalDevice: boolean,
  useForElectricityBilling: boolean,
): boolean {
  return countedViaExternalDevice || !useForElectricityBilling;
}
